import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';

// Extend ImportMeta for Vite environment variables
declare global {
  interface ImportMeta {
    env: {
      VITE_API_BASE_URL?: string;
    };
  }
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Types
export interface ApiError {
  timestamp: string;
  status: number;
  code: string;
  message: string;
  details?: Array<{ field: string; message: string }>;
  requestId: string;
}

// Instance Axios configurée
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Pour les cookies de refresh token
});

// Interceptor pour ajouter le token d'accès
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Le token d'accès est stocké en mémoire (dans un store React par exemple)
    // Ici on récupère depuis le localStorage ou un store global
    const token = localStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor pour gérer les erreurs 401 (token expiré)
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    // Issue #74 : ne JAMAIS tenter de refresh ni rediriger sur l'échec de /auth/login.
    // Sinon un mot de passe erroné (401 légitime) déclenche un refresh, échoue, et
    // recharge la page en /login?expired=1 : l'état du store est détruit et le message
    // « identifiants invalides » n'est jamais affiché. L'utilisateur croit que son compte
    // est cassé. On laisse l'erreur remonter au store qui l'affiche.
    const url = error.config?.url ?? '';
    const isAuthAttempt = url.includes('/auth/login')
        || url.includes('/auth/refresh')
        || url.includes('/auth/mfa');

    if (error.response?.status === 401 && !isAuthAttempt) {
      // Tenter de rafraîchir le token
      try {
        const refreshResponse = await axios.post(
          `${BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );
        
        // Stocker le nouveau token
        const newToken = refreshResponse.data.accessToken;
        localStorage.setItem('access_token', newToken);
        
        // Réessayer la requête originale
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        if (originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        }
      } catch {
        // Refresh échoué sur une requête protégée : la session est réellement expirée.
        localStorage.removeItem('access_token');
        window.location.href = '/login?expired=1';
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
